import confetti from 'canvas-confetti';
import { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChefHat, Clock, Sparkles, Heart, Share2, RefreshCw, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { slugify, SITE_URL } from '@/lib/seo';
import { DishImage } from '@/components/DishImage';

const NON_VEG_KEYWORDS = [
  'chicken', 'mutton', 'lamb', 'beef', 'pork', 'fish', 'prawn', 'shrimp',
  'egg', 'bombil', 'kingfish', 'meen', 'choris', 'goan fish', 'fish curry',
  'fish fry', 'fish moilee', 'fish cutlet', 'tandoori chicken', 'butter chicken',
];

const isNonVeg = (recipe) => {
  const blob = `${recipe.name} ${(recipe.ingredients || []).join(' ')}`.toLowerCase();
  return NON_VEG_KEYWORDS.some((k) => blob.includes(k));
};

const minutesFromPrep = (prepStr) => {
  if (!prepStr) return 30;
  const m = String(prepStr).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 30;
};

const SAVED_KEY = 'masala-spin-saved-recipes';

export const DecideMyMeal = ({ recipes, onOpenRecipe }) => {
  const [diet, setDiet] = useState('any'); // any | veg | nonveg
  const [time, setTime] = useState('any'); // any | quick | normal | weekend
  const [result, setResult] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [savedIds, setSavedIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to parse saved recipes:', err);
      }
      return new Set();
    }
  });

  const eligible = useMemo(() => {
    if (!recipes?.length) return [];
    const mealCats = ['breakfast', 'lunch', 'dinner', 'crazy'];
    let pool = recipes.filter((r) => mealCats.includes(r.category));

    if (diet === 'veg') pool = pool.filter((r) => !isNonVeg(r));
    if (diet === 'nonveg') pool = pool.filter((r) => isNonVeg(r));

    if (time === 'quick') {
      pool = pool.filter((r) => minutesFromPrep(r.prep_time) <= 20);
    } else if (time === 'normal') {
      pool = pool.filter((r) => {
        const m = minutesFromPrep(r.prep_time);
        return m > 20 && m <= 45;
      });
    } else if (time === 'weekend') {
      pool = pool.filter((r) => minutesFromPrep(r.prep_time) > 45);
    }

    return pool;
  }, [recipes, diet, time]);

  const decide = useCallback(() => {
    if (!eligible.length) {
      toast.error('No recipes match these filters — try loosening them.');
      return;
    }
    setSpinning(true);
    setTimeout(() => {
      let pick;
      let attempts = 0;
      do {
        pick = eligible[Math.floor(Math.random() * eligible.length)];
        attempts += 1;
      } while (result && pick.id === result.id && attempts < 5);
      setResult(pick);
      setSpinning(false);
      // 🎉 Confetti burst on every reveal
      try {
        confetti({
          particleCount: 90,
          spread: 75,
          origin: { y: 0.55 },
          colors: ['#F97316', '#FBBF24', '#2D4A22', '#FFFFFF'],
          scalar: 0.9,
        });
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Confetti failed:', err);
        }
      }
    }, 700);
  }, [eligible, result]);

  const persistSaved = useCallback((nextSet) => {
    setSavedIds(nextSet);
    localStorage.setItem(SAVED_KEY, JSON.stringify(Array.from(nextSet)));
  }, []);

  const toggleSaved = useCallback((id) => {
    const next = new Set(savedIds);
    if (next.has(id)) {
      next.delete(id);
      toast('Removed from saved');
    } else {
      next.add(id);
      toast.success('Saved! Find it later in Saved Recipes.', { duration: 2200 });
    }
    persistSaved(next);
  }, [savedIds, persistSaved]);

  const shareWhatsApp = useCallback((recipe) => {
    const url = `${SITE_URL}/recipe/${slugify(recipe.name)}`;
    const text = `I'm cooking *${recipe.name}* tonight 🍳 (${recipe.prep_time} · ${recipe.calories} cal)\n\nFull recipe: ${url}\n\n— picked on Masala Spin`;
    const wa = `https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`;
    window.open(wa, '_blank', 'noopener,noreferrer');
  }, []);

  const isSaved = result ? savedIds.has(result.id) : false;

  return (
    <section
      className="relative min-h-[88vh] flex items-center justify-center px-6 py-16 overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at top left, #FEF3E2 0%, #FDFBF7 55%, #FFF 100%)',
      }}
      data-testid="decide-my-meal-hero"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="relative max-w-4xl w-full">
        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="prompt"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4 }}
              className="text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 bg-white/70 backdrop-blur rounded-full text-xs uppercase tracking-widest text-[#F97316] font-['Outfit'] font-semibold border border-[#F97316]/20 shadow-sm">
                <Sparkles className="w-3 h-3" /> Tap once · meal in seconds
              </span>
              <h1 className="font-['Playfair_Display'] text-5xl sm:text-6xl lg:text-7xl font-bold text-[#1C1917] tracking-tight leading-[1.05] mb-5">
                What should you<br />
                <span className="text-[#F97316]">cook today?</span>
              </h1>
              <p className="text-base sm:text-lg text-[#1C1917]/65 font-['Outfit'] max-w-xl mx-auto mb-10">
                Tap once for an easy Indian dinner idea — Maharashtrian, Goan, Kerala, millet &amp; oil-free recipes. Zero thinking required.
              </p>

              {/* Filters */}
              <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
                <FilterGroup
                  label="Diet"
                  value={diet}
                  onChange={setDiet}
                  options={[
                    { value: 'any', label: 'Any' },
                    { value: 'veg', label: '🌱 Veg' },
                    { value: 'nonveg', label: '🍗 Non-Veg' },
                  ]}
                  testid="decide-filter-diet"
                />
                <FilterGroup
                  label="Time"
                  value={time}
                  onChange={setTime}
                  options={[
                    { value: 'any', label: 'Any' },
                    { value: 'quick', label: '⚡ Quick' },
                    { value: 'normal', label: 'Normal' },
                    { value: 'weekend', label: '🍳 Weekend' },
                  ]}
                  testid="decide-filter-time"
                />
              </div>

              <Button
                onClick={decide}
                disabled={spinning}
                data-testid="decide-my-meal-button"
                className="px-12 py-8 text-xl font-['Outfit'] font-semibold rounded-full bg-gradient-to-r from-[#F97316] to-[#FBBF24] text-white shadow-[0_20px_60px_-15px_rgba(249,115,22,0.7)] hover:shadow-[0_25px_70px_-15px_rgba(249,115,22,0.9)] hover:-translate-y-1 transition-all duration-300"
              >
                {spinning ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" /> Picking…
                  </>
                ) : (
                  <>
                    <ChefHat className="w-6 h-6 mr-2" /> Decide My Meal
                  </>
                )}
              </Button>

              <p className="mt-5 text-sm text-[#1C1917]/45 font-['Outfit']">
                no signup, no nonsense
              </p>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.94, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ type: 'spring', stiffness: 220, damping: 22 }}
              className="bg-white rounded-3xl shadow-2xl border border-[#1C1917]/5 overflow-hidden"
              data-testid="decide-result-card"
            >
              <div className="grid md:grid-cols-2">
                {/* Image */}
                <div className="relative h-72 md:h-[420px]">
                  <DishImage
                    src={result.image_url}
                    alt={result.name}
                    fallbackInitial={result.name?.[0] || 'M'}
                    className="absolute inset-0 w-full h-full"
                    imgClassName="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent md:bg-gradient-to-r" />
                </div>

                {/* Details */}
                <div className="p-8 md:p-10 flex flex-col">
                  <p className="text-xs uppercase tracking-[0.25em] text-[#F97316] font-['Outfit'] font-semibold mb-2">
                    Tonight's pick
                  </p>
                  <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl font-bold text-[#1C1917] tracking-tight mb-4 leading-tight">
                    {result.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 mb-6 text-[#1C1917]/70 font-['Outfit']">
                    <span className="flex items-center gap-1.5">
                      <Clock className="w-4 h-4 text-[#F97316]" />
                      {result.prep_time}
                    </span>
                    <span className="text-[#1C1917]/30">·</span>
                    <span>{result.calories} cal</span>
                    {result.region && (
                      <>
                        <span className="text-[#1C1917]/30">·</span>
                        <span className="px-2 py-0.5 rounded-full bg-[#F97316]/10 text-[#F97316] text-xs font-medium">
                          {result.region}
                        </span>
                      </>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <Button
                      onClick={() => onOpenRecipe(result)}
                      data-testid="decide-cook-this-button"
                      className="bg-[#2D4A22] hover:bg-[#1C1917] text-white rounded-full py-6 font-['Outfit'] font-semibold"
                    >
                      <ChefHat className="w-4 h-4 mr-2" />
                      Cook this
                    </Button>
                    <Button
                      onClick={decide}
                      disabled={spinning}
                      data-testid="decide-spin-again-button"
                      variant="outline"
                      className="spin-again-btn relative overflow-hidden border-[#F97316] text-[#F97316] hover:bg-[#F97316] hover:text-white rounded-full py-6 font-['Outfit'] font-semibold hover:scale-[1.03] transition-transform"
                    >
                      <RefreshCw className="spin-again-icon w-4 h-4 mr-2" />
                      Spin again
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => toggleSaved(result.id)}
                      data-testid="decide-save-button"
                      variant="ghost"
                      className={`rounded-full py-5 font-['Outfit'] ${
                        isSaved
                          ? 'bg-[#FEF3E2] text-[#F97316] hover:bg-[#FEF3E2]'
                          : 'text-[#1C1917]/70 hover:text-[#F97316]'
                      }`}
                    >
                      <Heart className={`w-4 h-4 mr-2 ${isSaved ? 'fill-current' : ''}`} />
                      {isSaved ? 'Saved' : 'Save'}
                    </Button>
                    <Button
                      onClick={() => shareWhatsApp(result)}
                      data-testid="decide-share-whatsapp-button"
                      variant="ghost"
                      className="rounded-full py-5 font-['Outfit'] text-[#25D366] hover:text-white hover:bg-[#25D366]"
                    >
                      <Share2 className="w-4 h-4 mr-2" />
                      WhatsApp
                    </Button>
                  </div>

                  <p className="mt-6 text-center text-xs text-[#1C1917]/45 font-['Outfit'] flex items-center justify-center gap-1">
                    Not feeling it? Try another <ChevronDown className="w-3 h-3 animate-bounce" />
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

const FilterGroup = ({ label, value, onChange, options, testid }) => (
  <div className="inline-flex items-center bg-white/80 backdrop-blur rounded-full p-1 shadow-sm border border-[#1C1917]/5">
    <span className="px-3 text-xs uppercase tracking-wider text-[#1C1917]/45 font-['Outfit'] font-semibold">
      {label}
    </span>
    <div className="flex">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            data-testid={`${testid}-${opt.value}`}
            className={`px-4 py-2 text-sm font-['Outfit'] font-medium rounded-full transition-all ${
              active
                ? 'bg-[#1C1917] text-white shadow-sm'
                : 'text-[#1C1917]/60 hover:text-[#1C1917]'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

// Helper to expose saved recipe IDs for other components
export const useSavedRecipeIds = () => {
  const [ids, setIds] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(SAVED_KEY) || '[]'));
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to parse saved recipe IDs:', err);
      }
      return new Set();
    }
  });
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === SAVED_KEY) {
        try { setIds(new Set(JSON.parse(e.newValue || '[]'))); } catch (err) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('useSaved: failed to parse storage event', err);
          }
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return ids;
};
